/**
 * TriviaImport - Bulk Drive-folder importer for trivia archives.
 *
 * The user supplies a folder URL + venue + flags. Submitting POSTs to
 * /api/import/drive-folder, which returns a jobId. We then connect to the
 * SSE stream at /api/import/drive-folder/status/:jobId to render
 * per-event progress and the final report.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Flex,
  Grid,
  Card,
  Heading,
  Text,
  Button,
  Callout,
  Checkbox,
  Select,
  Table,
  TextField,
  Spinner,
} from '@radix-ui/themes';
import api, { fetchVenues } from '../../utils/api';
import config from '../../config';
import type { Venue } from '../../types';

interface EventReport {
  venueName: string;
  date: string | null;
  status: 'success' | 'partial' | 'failed' | 'skipped';
  counts: {
    rounds: number;
    questions: number;
    teams: number;
    audioUploaded: number;
    audioFailed: number;
  };
  warnings: string[];
  errors: string[];
}

interface RunReport {
  folderId: string;
  venueId: number | null;
  dryRun: boolean;
  force: boolean;
  startedAt: string;
  finishedAt: string | null;
  venueName: string | null;
  archives: number;
  events: EventReport[];
  aggregate: { success: number; partial: number; failed: number; skipped: number };
}

interface ProgressEvent {
  stage: string;
  index?: number;
  total?: number;
  eventLabel?: string;
  status?: string;
  file?: string;
  folderId?: string;
  venueName?: string;
  archiveCount?: number;
}

const TriviaImport: React.FC = () => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [folderUrl, setFolderUrl] = useState('');
  const [venueId, setVenueId] = useState<number | ''>('');
  const [dryRun, setDryRun] = useState(true);
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [progress, setProgress] = useState<ProgressEvent[]>([]);
  const [report, setReport] = useState<RunReport | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const sseRef = useRef<EventSource | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchVenues()
      .then((res) => {
        if (!mounted) return;
        setVenues(res.data ?? []);
      })
      .catch(() => toast.error('Failed to load venues'));
    return () => {
      mounted = false;
      sseRef.current?.close();
    };
  }, []);

  const closeStream = () => {
    sseRef.current?.close();
    sseRef.current = null;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!folderUrl.trim()) {
      toast.error('Folder URL is required');
      return;
    }
    if (!venueId) {
      toast.error('Select a venue');
      return;
    }
    setProgress([]);
    setReport(null);
    setErrorMsg(null);
    setSubmitting(true);
    closeStream();

    try {
      const res = await api.post<{ status: string; data: { jobId: string } }>(
        '/import/drive-folder',
        { folderUrl, venueId, dryRun, force }
      );
      const jobId = res.data.data.jobId;
      const url = `${config.apiBaseUrl}/import/drive-folder/status/${jobId}`;
      const es = new EventSource(url, { withCredentials: true });
      sseRef.current = es;

      es.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          setProgress((prev) => [...prev, data]);
        } catch {
          // ignore malformed event
        }
      };
      es.addEventListener('complete', (msg) => {
        try {
          const data = JSON.parse((msg as MessageEvent).data);
          setReport(data.report || null);
        } catch {
          // ignore
        }
        setSubmitting(false);
        closeStream();
      });
      es.addEventListener('error', (msg) => {
        try {
          const data = JSON.parse((msg as MessageEvent).data);
          setErrorMsg(data.error || 'Import failed');
        } catch {
          setErrorMsg('Connection lost');
        }
        setSubmitting(false);
        closeStream();
      });
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setErrorMsg(e.response?.data?.message || e.message || 'Submit failed');
      setSubmitting(false);
    }
  };

  return (
    <Flex direction="column" gap="4">
      <Box>
        <Heading size="6">Trivia Archive Import</Heading>
        <Text size="2" color="gray">
          Point at a Google Drive folder containing trivia archives. The importer walks the folder,
          parses each event&apos;s docx and xlsx, uploads audio to object storage, and writes one quiz +
          one liveEvent per event.
        </Text>
      </Box>

      <Grid columns={{ initial: '1', md: '2' }} gap="4">
        <Card>
          <Heading size="4" mb="3">Import Folder</Heading>
          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Drive Folder URL <Text color="red">*</Text>
                </Text>
                <TextField.Root
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/…"
                  value={folderUrl}
                  onChange={(e) => setFolderUrl(e.target.value)}
                  disabled={submitting}
                />
              </label>

              <Box>
                <Text as="div" size="2" weight="medium" mb="1">
                  Venue <Text color="red">*</Text>
                </Text>
                <Select.Root
                  value={venueId ? String(venueId) : ''}
                  onValueChange={(val) => setVenueId(val ? parseInt(val, 10) : '')}
                  disabled={submitting}
                >
                  <Select.Trigger placeholder="Select venue…" />
                  <Select.Content>
                    {venues.map((v) => (
                      <Select.Item key={v.id} value={String(v.id)}>
                        {v.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Box>

              <Flex gap="4" align="center" wrap="wrap">
                <Text as="label" size="2">
                  <Flex gap="2" align="center">
                    <Checkbox
                      checked={dryRun}
                      onCheckedChange={(c) => setDryRun(c === true)}
                      disabled={submitting}
                    />
                    Dry run (no DB writes)
                  </Flex>
                </Text>
                <Text as="label" size="2">
                  <Flex gap="2" align="center">
                    <Checkbox
                      checked={force}
                      onCheckedChange={(c) => setForce(c === true)}
                      disabled={submitting}
                    />
                    Force overwrite existing
                  </Flex>
                </Text>
              </Flex>

              <Button disabled={submitting} type="submit">
                {submitting ? <><Spinner size="1" />Running…</> : 'Run Import'}
              </Button>
            </Flex>
          </form>
        </Card>

        <Card>
          <Heading size="4" mb="3">Progress</Heading>
          {errorMsg && (
            <Callout.Root color="red" mb="3">
              <Callout.Text>{errorMsg}</Callout.Text>
            </Callout.Root>
          )}
          {progress.length === 0 && !report && (
            <Text size="2" color="gray" style={{ fontStyle: 'italic' }}>
              No run started yet.
            </Text>
          )}
          {progress.length > 0 && (
            <ul style={{ fontFamily: 'monospace', fontSize: 12, paddingLeft: 16, maxHeight: 240, overflowY: 'auto' }}>
              {progress.map((p, i) => (
                <li key={i}>{formatProgress(p)}</li>
              ))}
            </ul>
          )}
        </Card>
      </Grid>

      {report && <ReportView report={report} />}
    </Flex>
  );
};

function formatProgress(p: ProgressEvent): string {
  if (p.stage === 'traversing') return `Traversing folder ${p.folderId || ''}`;
  if (p.stage === 'plan') return `Found ${p.archiveCount} archive(s) in "${p.venueName}"`;
  if (p.stage === 'event-start') return `[${p.index}/${p.total}] ${p.eventLabel} – starting`;
  if (p.stage === 'event-end') return `[${p.index}/${p.total}] ${p.eventLabel} – ${p.status}`;
  if (p.stage === 'event-progress' && p.file) return `  · ${p.file}`;
  if (p.stage === 'done') return 'Run finished';
  return JSON.stringify(p);
}

const ReportView: React.FC<{ report: RunReport }> = ({ report }) => (
  <Card>
    <Heading size="4" mb="3">Report</Heading>
    <Text as="p" size="2" mb="1">
      Venue: <Text weight="bold">{report.venueName}</Text> · Mode:{' '}
      <Text weight="bold">{report.dryRun ? 'dry-run' : 'live'}</Text>
      {report.force ? ' (force)' : ''}
    </Text>
    <Text as="p" size="2" mb="3">
      <Text color="green">{report.aggregate.success} success</Text>{' · '}
      <Text color="amber">{report.aggregate.partial} partial</Text>{' · '}
      <Text color="red">{report.aggregate.failed} failed</Text>{' · '}
      <Text color="gray">{report.aggregate.skipped} skipped</Text>
    </Text>
    <Table.Root variant="surface" size="1">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Rounds</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Q</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Teams</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Audio (✓ / ✗)</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Warnings</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Errors</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {report.events.map((e, i) => (
          <Table.Row key={i}>
            <Table.Cell>{e.date ? e.date.slice(0, 10) : '—'}</Table.Cell>
            <Table.Cell>{e.status}</Table.Cell>
            <Table.Cell>{e.counts.rounds}</Table.Cell>
            <Table.Cell>{e.counts.questions}</Table.Cell>
            <Table.Cell>{e.counts.teams}</Table.Cell>
            <Table.Cell>{e.counts.audioUploaded} / {e.counts.audioFailed}</Table.Cell>
            <Table.Cell>{e.warnings.length}</Table.Cell>
            <Table.Cell>{e.errors.join('; ') || '—'}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  </Card>
);

export default TriviaImport;
